import { useMemo } from 'react'
import chunk from 'lodash/chunk'
import isNumber from 'lodash/isNumber'
import sortBy from 'lodash/sortBy'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import uniq from 'lodash/uniq'
import type { DocumentList } from '@klear/document/trader-document'
import { trpc } from '@klear/ui/lib/trpc'
import { ORDER_EVENT_DOCUMENT_LIST_MAX_IDS, ORDER_EVENT_DOCUMENT_LIST_PAGE_SIZE } from '@klear/order-event/constants'
import { filenameForContentType } from '@klear/upload/content-type'
import { orderEventDocumentsByEvent, type OrderEventDocumentRef } from '@klear/upload/order-event-documents'

type DocumentBatchResult = { data?: DocumentList; isFetching: boolean }

function filesOnEvents(list: DocumentList | undefined, wanted: ReadonlySet<number>): OrderEventDocumentRef[] {
  return (list?.rows ?? []).flatMap(({ document }) =>
    document.references.flatMap((reference) =>
      reference.kind === 'order_event' && wanted.has(reference.id)
        ? [
            {
              id: reference.fileId,
              documentKey: toString(document.id),
              orderEventId: reference.id,
              name: filenameForContentType(document.name, document.contentType),
              traderId: document.traderId
            }
          ]
        : []
    )
  )
}

export function useOrderEventDocuments(
  orderEventIds: readonly (number | null | undefined)[],
  traderId?: number
): { documentsFor: (orderEventId: number | null | undefined) => OrderEventDocumentRef[]; isFetching: boolean } {
  const wanted = useMemo(
    () => uniq(orderEventIds.filter((id): id is number => isNumber(id) && id > 0)).sort((a, b) => a - b),
    [orderEventIds]
  )
  const batches = useMemo(() => chunk(wanted, ORDER_EVENT_DOCUMENT_LIST_MAX_IDS), [wanted])
  const combine = useMemo(() => {
    const wantedSet = new Set(wanted)
    return (results: readonly DocumentBatchResult[]) => ({
      byEvent: orderEventDocumentsByEvent(
        sortBy(
          results.flatMap((result) => filesOnEvents(result.data, wantedSet)),
          (file) => toNumber(file.id)
        )
      ),
      isFetching: results.some((result) => result.isFetching)
    })
  }, [wanted])

  const { byEvent, isFetching } = trpc.useQueries(
    (t) =>
      batches.map((ids) =>
        t.documents.list({
          orderEventIds: ids,
          ...(traderId != null && { traderId }),
          limit: ORDER_EVENT_DOCUMENT_LIST_PAGE_SIZE
        })
      ),
    { combine }
  )

  return useMemo(
    () => ({
      documentsFor: (orderEventId: number | null | undefined) =>
        orderEventId == null ? EMPTY : (byEvent.get(orderEventId) ?? EMPTY),
      isFetching
    }),
    [byEvent, isFetching]
  )
}

const EMPTY: OrderEventDocumentRef[] = []
